import { Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { ReactElement, useCallback } from 'react';
import { Column } from 'react-data-grid';
import {
  navigateToPipelineIssues,
  navigateToSpecificApp,
} from '../../common/routing/app-navigator';
import CustomLink from '../../elements/Aggregation/elements/Link';
import MultiLineEllipsis from '../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { PipelineSummary } from '../pipeline-summary-types';
import { FaCheck } from 'react-icons/fa';
import { ImBlocked } from 'react-icons/im';
import { GiCctvCamera } from 'react-icons/gi';
import IssueBadge from '../../common/components/IssueBadge';
import { stopPropagation } from '../../common/common-utils';
import ApplicationCell from '../../elements/TableCells/ApplicationCell';
import { MdAccountCircle as AccountCircleIcon } from 'react-icons/md';
import BranchColumn from '../components/BranchColumn';
import { makeStyles } from 'tss-react/mui';
import { CategoryRiskNames } from 'common-types';

export const usePipelineSummaryHeader = (classes?: {
  textCenter?: string;
  flexCenter?: string;
  headerCell?: string;
  noPadding?: string;
}) => {
  const baseColumn: Partial<Column<PipelineSummary>> = {
    headerCellClass: classes?.headerCell,
  };

  const { classes: styles } = useStyles();

  const onIssueChipClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, jobId: string) => {
      const { name } = event.currentTarget.dataset;
      event.stopPropagation();
      event.preventDefault();
      name &&
        navigateToPipelineIssues({
          criticality: [CategoryRiskNames[name]],
          jobNumber: [jobId],
        });
    },
    [],
  );

  const openApps = useCallback(appId => {
    appId && navigateToSpecificApp(appId);
  }, []);

  const columns: Column<PipelineSummary>[] = [
    {
      ...baseColumn,
      key: 'result',
      name: '',
      width: 50,
      minWidth: 50,
      cellClass: classes?.textCenter,
      formatter: ({ row }) => {
        let icon: ReactElement | null = null;
        switch (row.result) {
          case 'Block':
            icon = <ImBlocked size={16} color='red' />;
            break;
          case 'Passed':
            icon = <FaCheck size={16} color='green' />;
            break;
          case 'Monitor':
            icon = <GiCctvCamera size={20} color='#FFA500' />;
            break;
        }
        return (
          <Tooltip title={row.result}>
            <div className={classes?.textCenter}>{icon!}</div>
          </Tooltip>
        );
      },
    },
    {
      ...baseColumn,
      key: 'appName',
      name: 'App Name',
      width: 200,
      formatter: ({ row }) => {
        const [app, ...otherApps] = [...row.apps];
        return (
          <Tooltip
            title={
              otherApps.length !== 0 ? (
                <div className={styles.appsTooltip}>
                  {otherApps.map(({ appId, appName, appType }) => (
                    <ApplicationCell
                      sx={{ cursor: 'pointer' }}
                      appName={appName}
                      appType={appType}
                      variant='caption'
                      color='#5059FF'
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        openApps(appId);
                      }}
                    />
                  ))}
                </div>
              ) : (
                ''
              )
            }>
            {app ? (
              <div className={styles.appCell}>
                <ApplicationCell
                  appName={`${app.appName && app.appName} ${
                    otherApps.length !== 0 ? ` [+${otherApps.length}]` : ''
                  }`}
                  appType={app.appType && app.appType}
                  variant='caption'
                  color='#5059FF'
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    openApps(app.appId);
                  }}
                />
              </div>
            ) : (
              <div className={styles.appCell}></div>
            )}
          </Tooltip>
        );
      },
    },
    {
      ...baseColumn,
      key: 'branch',
      name: 'Scanned Branch',
      formatter: ({ row }) => (
        <BranchColumn
          eventType={row.eventType}
          targetBranch={row.targetBranch}
          sourceBranch={row.sourceBranch}
          pullRequestUrl={row.pullRequestUrl}
          pullRequestId={row.pullRequestId}
        />
      ),
    },
    {
      ...baseColumn,
      key: 'jobId',
      name: 'Job ID',
      width: 130,
      formatter: ({ row }) => (
        <MultiLineEllipsis lines={2}>
          <CustomLink
            variant='caption'
            color='text.secondary'
            onClick={stopPropagation}
            href={row.jobUrl}
            value={row.jobId}
          />
        </MultiLineEllipsis>
      ),
    },
    {
      ...baseColumn,
      key: 'jobTriggeredBy',
      name: 'Triggered By',
      width: 150,
      formatter: ({ row }) => (
        <div className={classes?.textCenter}>
          <div className={classes?.textCenter}>
            <AccountCircleIcon color='#6837FF' size={16} />
          </div>

          <MultiLineEllipsis
            sx={{ gap: 0.5 }}
            variant='caption'
            color='text.secondary'
            lines={2}>
            {row.jobTriggeredBy}
          </MultiLineEllipsis>
        </div>
      ),
    },
    {
      ...baseColumn,
      key: 'issues',
      name: 'Issues',
      width: 170,
      formatter: ({ row }) => {
        return row.result !== 'Passed' ? (
          <IssueBadge
            severitiesAlerts={row.issues}
            handleClick={event => onIssueChipClick(event, row.jobId)}
          />
        ) : (
          <Tooltip
            title={
              <Typography variant='caption' color='text.primary'>
                No issues
              </Typography>
            }>
            <div className={classes?.textCenter}>
              <FaCheck size={16} color='green' />
            </div>
          </Tooltip>
        );
      },
    },
    {
      ...baseColumn,
      key: 'totalIssues',
      name: 'Blocking',
      width: 120,
      formatter: ({ row }) => {
        return row.result !== 'Passed' ? (
          <Typography variant='caption' color='text.secondary'>
            {row.totalBlockingIssues} out of {row.totalIssues}
          </Typography>
        ) : (
          <Tooltip
            title={
              <Typography variant='caption' color='text.primary'>
                No issues
              </Typography>
            }>
            <div className={classes?.textCenter}>
              <FaCheck size={16} color='green' />
            </div>
          </Tooltip>
        );
      },
    },
    {
      ...baseColumn,
      key: 'jobTriggeredAt',
      name: 'Date',
      width: 140,
      formatter: ({ row }) => (
        <Tooltip title={row.jobTriggeredAt}>
          <div>
            <MultiLineEllipsis
              variant='caption'
              color='text.secondary'
              lines={2}>
              {formatDistanceToNow(new Date(row.jobTriggeredAt || 0), {
                addSuffix: true,
              })}
            </MultiLineEllipsis>
          </div>
        </Tooltip>
      ),
    },
  ];

  return columns;
};

const useStyles = makeStyles()({
  appsTooltip: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 125,
    padding: 8,
  },
  appCell: {
    display: 'flex',
    alignItems: 'center',
  },
});
